import { useCurrentAccount } from "@mysten/dapp-kit-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { truncate } from "truncate-ethereum-address";

import { config } from "@/config";
import { useClaimsContext } from "@/context/claims/useClaimsContext";
import { useSuiQueryContext } from "@/context/SUIQuery/useSuiQueryContext";
import { useSuiTransactions, useSuiTransactionTimestamps } from "@/hooks/sui/queries";
import { useRoutes } from "@/hooks/useRoutes/useRoutes";
import { formatClaimableAmount, TRUTH_DECIMALS } from "@/lib/currency/tokenAmount";
import date from "@/lib/date";
import str from "@/lib/str";

import { OwnedTransactionsForPackageProps } from "./OwnedTransactionsForPackage.types";

// MIGRATION: the core API filter takes a single `::`-delimited Move path. The
// old JSON-RPC filter object also carried `FromAddress`, but the builder dropped
// it — sender matching has always happened client-side below.
const PURCHASE_LICENSE = `${config.packageId}::swarm_license_sale::purchase_license`;

const CREATE_LICENSE_EVENT = `${config.packageId}::swarm_license_sale::CreateLicenseEvent`;

/**
 * User-facing labels for claim statuses.
 *
 * "quarantined" is the internal state the airdrop worker sets for a claim it
 * refuses to pay. Shown verbatim it reads like an accusation, so it surfaces as
 * "cancelled" -- which is also what actually happened from the user's side: the
 * claim will not be sent, and the amount is back in their claimable balance to
 * request again.
 */
const STATUS_LABELS: Record<string, string> = {
  quarantined: "cancelled",
};

/**
 * Net $TRUTH the claim moves to the user's wallet.
 *
 * A completed claim already has the settled figure in `total_claimed`. Anything
 * not yet paid only has `total_requested`, which is the gross -- the worker
 * deducts the burn penalty before transferring (see the burn split in
 * lib/airdrop-worker/src/db.ts lockPending), so the same deduction is applied
 * here rather than promising the user the pre-penalty amount.
 */
function netClaimAmount(claim: {
  status: string;
  total_claimed?: number | string | null;
  total_requested?: number | string | null;
  burn_penalty?: number | string | null;
}): number {
  if (claim.status === "completed") return Number(claim.total_claimed ?? 0);

  const requested = Number(claim.total_requested ?? 0);
  const penalty = Number(claim.burn_penalty ?? 0);

  if (!Number.isFinite(requested) || requested <= 0) return 0;
  if (!Number.isFinite(penalty) || penalty <= 0) return requested;

  return requested - (requested * penalty) / 100;
}

export const OwnedTransactionsForPackage: React.FC<OwnedTransactionsForPackageProps> = () => {
  const routes = useRoutes();

  const account = useCurrentAccount();

  const { transactionOutput, purchaseLicenseForm } = useSuiQueryContext();

  const { getClaimsByUserAddress, getClaimsByUserAddressResponse } = useClaimsContext();

  const pollingTimerRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const isPollingRef = useRef(false);

  // Pagination state - must be declared before any conditional returns
  const [currentPage, setCurrentPage] = useState(1);

  const { data, isPending, error, refetch, isFetched } = useSuiTransactions(
    {
      filter: { function: PURCHASE_LICENSE },
      order: "descending",
      // `transaction` is needed for the sender, `events` for the referral code.
      include: { events: true, transaction: true },
    },
    {
      enabled: !!config.packageId,
    },
  );

  const txs =
    useMemo(() => {
      if (!data?.transactions) return [];

      const ownedTransactionsForPackage = [];

      // MIGRATION: list items are a `{ $kind, Transaction | FailedTransaction }`
      // union, and the sender now lives at `transaction.sender` (was
      // `transaction.data.sender`).
      for (const result of data.transactions) {
        const tx = result.Transaction;

        if (!tx?.events) continue;

        if (tx.transaction?.sender === account?.address) {
          ownedTransactionsForPackage.push(tx);
        }
      }

      return ownedTransactionsForPackage;
    }, [data?.transactions, account]) || [];

  // MIGRATION: the core transaction shape dropped `timestampMs`, so timestamps
  // for the (few) transactions we actually render are fetched separately.
  const { data: timestamps } = useSuiTransactionTimestamps(txs.map((tx) => tx.digest));

  useEffect(() => {
    if (!txs || txs.length === 0) return;

    for (const tx of txs) {
      if (!tx.events) break;

      for (const event of tx.events) {
        if (event.eventType === CREATE_LICENSE_EVENT) {
          const referralCode = (event.json as { referral_code: string } | null)?.referral_code;

          if (referralCode) {
            purchaseLicenseForm.setValue("referralCode", referralCode);
          }

          break;
        }
      }

      break;
    }
  }, [txs]);

  useEffect(() => {
    if (!isFetched) return;

    refetch();
  }, [account, isFetched, transactionOutput.purchaseLicense.onSuccess?.digest]);

  // Function to start polling
  const startPolling = () => {
    if (!account?.address || isPollingRef.current) return;

    const tick = async () => {
      if (!isPollingRef.current || !account?.address) return;

      await getClaimsByUserAddress({ user_address: account.address });
    };

    // Initial fetch
    tick();

    // Start polling every 20 seconds
    isPollingRef.current = true;
    pollingTimerRef.current = setInterval(tick, 20000);
  };

  // Function to stop polling
  const stopPolling = () => {
    if (pollingTimerRef.current) {
      clearInterval(pollingTimerRef.current);
      pollingTimerRef.current = undefined;
    }
    isPollingRef.current = false;
  };

  // Effect to manage polling based on account address
  useEffect(() => {
    if (!account?.address) {
      stopPolling();

      return;
    }

    // Start polling when account is available
    startPolling();

    return () => {
      stopPolling();
    };
  }, [account?.address]);

  // Effect to manage polling based on pending transactions
  useEffect(() => {
    if (!account?.address || !getClaimsByUserAddressResponse?.claims) return;

    const hasPendingTransactions = getClaimsByUserAddressResponse.claims.some(
      (claim) => claim.status === "pending" || claim.status === "processing",
    );

    if (hasPendingTransactions) {
      // Start polling if we have pending transactions and we're not already polling
      if (!isPollingRef.current) {
        startPolling();
      }
    } else {
      // Stop polling if no pending transactions
      stopPolling();
    }
  }, [getClaimsByUserAddressResponse?.claims, account?.address]);

  // Combine and sort all transactions - must be computed before using in useEffect
  const allTransactions = useMemo(
    () =>
      [
        ...(txs?.map((tx) => ({
          // Row identity. Not the digest alone: claim rows below have no digest
          // until they settle, so several would collide on "" and React would
          // reconcile them onto each other.
          key: `purchase:${tx.digest}`,
          type: "purchase" as const,
          digest: tx.digest,
          owner: tx.transaction?.sender || "",
          package: config.packageId || "",
          packageName: "swarm_license_sale::purchase_license",
          status: "success",
          // A license purchase moves SUI, not $TRUTH, so there is no amount to
          // show in this column.
          amount: null as number | null,
          // `null` (not 0) while unresolved — the renderer treats falsy as "—"
          // rather than printing an epoch date.
          timestamp: timestamps?.[tx.digest] ?? null,
          data: tx,
        })) || []),
        ...(getClaimsByUserAddressResponse?.claims.map((claim) => ({
          key: `claim:${claim.id}`,
          type: "claim" as const,
          digest: claim.sui_transaction_digest || "",
          owner: claim.user_address || "",
          package: config.truthCoinPackageId || "",
          packageName: "swarm_network_token::SWARM_NETWORK_TOKEN",
          status: claim.status,
          amount: netClaimAmount(claim) as number | null,
          timestamp: claim.claimed_at ? new Date(claim.claimed_at).getTime() : null,
          data: claim,
        })) || []),
      ].sort((a, b) => Number(b.timestamp || 0) - Number(a.timestamp || 0)),
    // `timestamps` resolves after `txs` (separate query), so it must be a dep or
    // rows would stay pinned at timestamp 0 and sort wrongly.
    [txs, timestamps, getClaimsByUserAddressResponse?.claims],
  );

  // Reset to page 1 when transactions change
  useEffect(() => {
    setCurrentPage(1);
  }, [allTransactions.length]);

  if (error) {
    // eslint-disable-next-line no-console
    console.error(error);

    return null;
  }

  if (isPending) {
    return null;
  }

  // Helper function to render status badge
  const renderStatusBadge = (status: string) => {
    const statusConfig = {
      success: "border-green-500/30 bg-green-500/10 text-green-400",
      pending: "border-yellow-500/30 bg-yellow-500/10 text-yellow-400",
      processing: "border-blue-500/30 bg-blue-500/10 text-blue-400",
      failed: "border-red-500/30 bg-red-500/10 text-red-400",
      // Neutral rather than red: nothing went wrong for the user and nothing was
      // lost, the claim simply will not be sent.
      quarantined: "border-gray-500/30 bg-gray-500/10 text-gray-300",
    };

    const colorClass =
      statusConfig[status.toLowerCase() as keyof typeof statusConfig] ||
      "border-gray-500/30 bg-gray-500/10 text-gray-400";

    const label = STATUS_LABELS[status.toLowerCase()] ?? status;

    return (
      <span
        className={`inline-flex items-center gap-1 rounded border px-2 py-1 text-[10px] font-bold uppercase tracking-wider ${colorClass}`}
        id={`transaction-status-badge-${status.toLowerCase()}`}
      >
        {status === "pending" || status === "processing" ? (
          <svg className="h-3 w-3 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              fill="currentColor"
            />
          </svg>
        ) : null}
        {label}
      </span>
    );
  };

  // Pagination logic
  const rowsPerPage = 10;
  const totalPages = Math.ceil(allTransactions.length / rowsPerPage);

  // Get current page transactions
  const paginatedTransactions = allTransactions.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage,
  );

  if (allTransactions.length === 0) {
    return (
      <div
        className="rounded-sm border border-gray-800 bg-background p-8 text-center"
        id="transaction-history-empty"
      >
        <svg
          className="mx-auto mb-4 h-12 w-12 text-gray-600"
          fill="none"
          id="transaction-history-empty-icon"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
          />
        </svg>
        <h4 className="mb-2 text-gray-400" id="transaction-history-empty-title">
          No Transactions Yet
        </h4>
        <p className="mb-0 text-sm text-gray-600" id="transaction-history-empty-hint">
          Your transaction history will appear here
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3" id="transaction-history">
      <div
        className="overflow-x-auto rounded-sm border border-gray-800"
        id="transaction-history-table-wrapper"
      >
        <table className="w-full" id="transaction-history-table">
          <thead
            className="border-b border-gray-800 bg-gray-900/50"
            id="transaction-history-table-head"
          >
            <tr id="transaction-history-header-row">
              <th
                className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-gray-500"
                id="transaction-history-header-type"
              >
                Type
              </th>
              <th
                className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-gray-500"
                id="transaction-history-header-digest"
              >
                Digest
              </th>
              <th
                className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-gray-500"
                id="transaction-history-header-owner"
              >
                Owner
              </th>
              <th
                className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-gray-500"
                id="transaction-history-header-package"
              >
                Package
              </th>
              <th
                className="px-4 py-3 text-right text-[10px] font-bold uppercase tracking-wider text-gray-500"
                id="transaction-history-header-amount"
              >
                Amount
              </th>
              <th
                className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-gray-500"
                id="transaction-history-header-status"
              >
                Status
              </th>
              <th
                className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-gray-500"
                id="transaction-history-header-date"
              >
                Date
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-800" id="transaction-history-table-body">
            {paginatedTransactions.map((transaction) => (
              <tr
                key={transaction.key}
                className="transition-colors hover:bg-gray-900/30"
                id={`transaction-history-row-${transaction.key}`}
              >
                <td
                  className="px-4 py-3 text-sm text-white"
                  id={`transaction-history-row-${transaction.key}-type`}
                >
                  {transaction.type === "purchase" ? "Purchase License" : "Claim $TRUTH"}
                </td>
                <td className="px-4 py-3" id={`transaction-history-row-${transaction.key}-digest`}>
                  {/* A claim has no digest until it settles on chain, and a
                      cancelled one never gets there. Linking anyway produced a
                      blank link to an explorer URL with no digest in it. */}
                  {transaction.digest ? (
                    <a
                      className="font-mono text-xs text-blue-400 no-underline transition-colors hover:text-blue-300"
                      href={routes.sui.explorerUrl({
                        path: `/txblock/${transaction.digest}`,
                      })}
                      id={`transaction-history-row-${transaction.key}-digest-link`}
                      target="_blank"
                    >
                      {str.format.truncateByHalf(transaction.digest)}
                    </a>
                  ) : (
                    <span
                      className="font-mono text-xs text-gray-500"
                      id={`transaction-history-row-${transaction.key}-digest-empty`}
                    >
                      -
                    </span>
                  )}
                </td>
                <td className="px-4 py-3" id={`transaction-history-row-${transaction.key}-owner`}>
                  <a
                    className="font-mono text-xs text-blue-400 no-underline transition-colors hover:text-blue-300"
                    href={routes.sui.explorerUrl({
                      path: `/account/${transaction.owner}`,
                    })}
                    id={`transaction-history-row-${transaction.key}-owner-link`}
                    target="_blank"
                  >
                    {truncate(transaction.owner)}
                  </a>
                </td>
                <td className="px-4 py-3" id={`transaction-history-row-${transaction.key}-package`}>
                  <a
                    className="font-mono text-xs text-blue-400 no-underline transition-colors hover:text-blue-300"
                    href={routes.sui.explorerUrl({
                      path: `/package/${transaction.package}`,
                    })}
                    id={`transaction-history-row-${transaction.key}-package-link`}
                    target="_blank"
                  >
                    {truncate(transaction.package)}::
                    {str.format.truncateByHalf(transaction.packageName)}
                  </a>
                </td>
                <td
                  className="whitespace-nowrap px-4 py-3 text-right font-mono text-xs"
                  id={`transaction-history-row-${transaction.key}-amount`}
                >
                  {transaction.amount === null ? (
                    <span
                      className="text-gray-500"
                      id={`transaction-history-row-${transaction.key}-amount-empty`}
                    >
                      -
                    </span>
                  ) : (
                    <span
                      className={
                        // Struck through when the transfer will not happen, so the
                        // figure reads as "would have been" without needing a
                        // second column to say so.
                        transaction.status === "quarantined"
                          ? "text-gray-500 line-through"
                          : "text-white"
                      }
                      id={`transaction-history-row-${transaction.key}-amount-value`}
                    >
                      {/* The exact claimed amount to all 8 decimals, truncated
                          and never rounded up: a receipt has to name what
                          actually lands in the wallet, and 2 decimals hid the
                          rest of the claim. */}
                      {formatClaimableAmount(transaction.amount, TRUTH_DECIMALS)}{" "}
                      <span
                        className="text-gray-500"
                        id={`transaction-history-row-${transaction.key}-amount-ticker`}
                      >
                        TRUTH
                      </span>
                    </span>
                  )}
                </td>
                <td className="px-4 py-3" id={`transaction-history-row-${transaction.key}-status`}>
                  {renderStatusBadge(transaction.status)}
                </td>
                <td
                  className="px-4 py-3 text-xs text-gray-500"
                  id={`transaction-history-row-${transaction.key}-date`}
                >
                  {transaction.timestamp
                    ? date.toFriendlyDatetimeShort(transaction.timestamp)
                    : "-"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div
          className="flex items-center justify-between rounded-sm border border-gray-800 bg-background px-4 py-3"
          id="transaction-history-pagination"
        >
          <div className="text-xs text-gray-500" id="transaction-history-pagination-summary">
            Showing {(currentPage - 1) * rowsPerPage + 1} to{" "}
            {Math.min(currentPage * rowsPerPage, allTransactions.length)} of{" "}
            {allTransactions.length} transactions
          </div>
          <div className="flex items-center gap-2" id="transaction-history-pagination-controls">
            <button
              className="rounded border border-gray-700 bg-gray-900/50 px-3 py-1 text-xs font-bold uppercase tracking-wider text-gray-400 transition-colors hover:border-gray-600 hover:bg-gray-900 hover:text-white disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-gray-700 disabled:hover:bg-gray-900/50 disabled:hover:text-gray-400"
              disabled={currentPage === 1}
              id="transaction-history-previous-page-button"
              type="button"
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
            >
              Previous
            </button>
            <span className="text-xs text-gray-500" id="transaction-history-page-indicator">
              Page {currentPage} of {totalPages}
            </span>
            <button
              className="rounded border border-gray-700 bg-gray-900/50 px-3 py-1 text-xs font-bold uppercase tracking-wider text-gray-400 transition-colors hover:border-gray-600 hover:bg-gray-900 hover:text-white disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-gray-700 disabled:hover:bg-gray-900/50 disabled:hover:text-gray-400"
              disabled={currentPage === totalPages}
              id="transaction-history-next-page-button"
              type="button"
              onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
